import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { History, User, Clock } from "lucide-react";
import { toast } from "sonner";

interface EvaluationHistoryEntry {
  id: string;
  change_type: string;
  changes: any;
  created_at: string;
  profiles: {
    full_name: string;
    email: string;
  };
}

interface EvaluationHistoryProps {
  evaluationId: string;
  isOpen: boolean;
  onClose: () => void;
}

const EvaluationHistory = ({ evaluationId, isOpen, onClose }: EvaluationHistoryProps) => {
  const [history, setHistory] = useState<EvaluationHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && evaluationId) {
      fetchHistory();
    }
  }, [isOpen, evaluationId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('evaluation_history')
        .select(`
          id,
          change_type,
          changes,
          created_at,
          profiles!inner (
            full_name,
            email
          )
        `)
        .eq('evaluation_id', evaluationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error("Fehler beim Laden der Historie");
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'created': return 'Erstellt';
      case 'updated': return 'Bearbeitet';
      case 'deleted': return 'Gelöscht';
      default: return type;
    }
  };

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'created': return 'bg-success/10 text-success';
      case 'updated': return 'bg-primary/10 text-primary';
      case 'deleted': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatChanges = (entry: EvaluationHistoryEntry) => {
    if (entry.change_type === 'updated' && entry.changes?.old && entry.changes?.new) {
      const changes = [];
      const old = entry.changes.old;
      const newData = entry.changes.new;

      if (old.total_score !== newData.total_score) {
        changes.push(`Punkte: ${old.total_score} → ${newData.total_score}`);
      }
      if (old.performance_level !== newData.performance_level) {
        changes.push(`Bewertung: ${old.performance_level} → ${newData.performance_level}`);
      }

      return changes.length > 0 ? changes.join(', ') : 'Keine sichtbaren Änderungen';
    }
    return '';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Änderungshistorie
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Laden...</p>
          ) : history.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Keine Historie vorhanden
            </p>
          ) : (
            history.map((entry) => (
              <Card key={entry.id} className="border-l-4" style={{ borderLeftColor: entry.change_type === 'created' ? 'hsl(var(--success))' : entry.change_type === 'updated' ? 'hsl(var(--primary))' : 'hsl(var(--destructive))' }}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getChangeTypeColor(entry.change_type)}`}>
                          {getChangeTypeLabel(entry.change_type)}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(entry.created_at).toLocaleDateString('de-DE', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{entry.profiles.full_name}</span>
                        <span className="text-muted-foreground">({entry.profiles.email})</span>
                      </div>

                      {entry.change_type === 'updated' && (
                        <div className="text-sm text-muted-foreground mt-2">
                          {formatChanges(entry)}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EvaluationHistory;
